/* Mirror the server-rendered cart subtotal and activate its existing checkout
   button. Never create a second form or bypass checkout/validation handlers. */
if (!customElements.get('mam-mobile-cart')) {
  customElements.define('mam-mobile-cart', class extends HTMLElement {
    connectedCallback() {
      this.footer = this.closest('#main-cart-footer');
      this.items = document.getElementById('main-cart-items');
      if (!this.footer || !this.items) return;
      this.button = this.querySelector('button');
      this.amount = this.querySelector('[data-cart-sticky-amount]');
      this.media = matchMedia('(max-width: 749px)');
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
      };
      this.button.addEventListener('click', () => {
        this.sync();
        if (!this.button.disabled) this.original?.click();
      }, options);
      // Block the new CTA immediately, including the theme's debounce interval.
      const pendingChange = event => {
        if (event.target.matches('input[name="updates[]"]')) {
          this.pending = true;
          this.schedule();
        }
      };
      this.items.addEventListener('input', pendingChange, options);
      this.items.addEventListener('change', pendingChange, options);
      window.addEventListener('resize', this.schedule, options);
      this.media.addEventListener('change', this.schedule, options);
      window.visualViewport?.addEventListener('resize', this.schedule, options);
      document.addEventListener('focusin', this.schedule, options);
      document.addEventListener('focusout', this.schedule, options);
      this.observer = new MutationObserver(records => {
        // A fast response can add/remove the loading class in one frame.
        // oldValue lets us observe completion even when the final class is clear.
        if (records.some(record => record.type === 'attributes' &&
            record.attributeName === 'class' && record.target === this.items &&
            (this.items.classList.contains('cart__items--disabled') ||
             record.oldValue?.includes('cart__items--disabled')))) {
          this.sawLoading = true;
        }
        this.schedule();
      });
      const footerContent = this.footer.querySelector('.cart__footer');
      if (footerContent) this.observer.observe(footerContent, {subtree: true, childList: true, characterData: true, attributes: true});
      this.observer.observe(this.footer, {attributes: true, attributeFilter: ['class']});
      this.observer.observe(this.items, {subtree: true, childList: true, attributes: true, attributeOldValue: true});
      this.observer.observe(document.body, {attributes: true, attributeFilter: ['class']});
      this.sizeObserver = new ResizeObserver(this.schedule);
      this.sizeObserver.observe(this);
      if (footerContent) this.sizeObserver.observe(footerContent);
      this.sync();
    }
    sync() {
      this.original = this.footer.querySelector('.cart__checkout-button[name="checkout"]');
      const source = this.footer.querySelector('.totals__subtotal-value');
      const subtotal = source?.textContent.trim() || '';
      if (this.amount.textContent !== subtotal) this.amount.textContent = subtotal;
      const loading = this.items.classList.contains('cart__items--disabled');
      if (loading) this.sawLoading = true;
      else if (this.sawLoading) { this.pending = false; this.sawLoading = false; }
      const busy = loading || !!this.pending;
      const empty = this.footer.classList.contains('is-empty') || !this.items.querySelector('.cart-item');
      this.button.disabled = busy || empty || !this.original || this.original.disabled || this.original.getAttribute('aria-disabled') === 'true';
      this.button.setAttribute('aria-busy', String(busy));
      const inputFocused = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]),textarea,select,[contenteditable="true"]');
      const visible = this.media.matches && !empty && !!subtotal && !!this.original &&
        !inputFocused && !document.body.classList.contains('overflow-hidden');
      this.hidden = !visible;
      document.body.classList.toggle('mam-mobile-cart-visible', visible);
      document.body.classList.toggle('mam-mobile-cart-enabled', this.media.matches && !empty);
      if (visible) {
        const size = `${this.getBoundingClientRect().height}px`;
        if (document.body.style.getPropertyValue('--mam-mobile-cart-height') !== size) {
          document.body.style.setProperty('--mam-mobile-cart-height', size);
        }
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.sizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      document.body.classList.remove('mam-mobile-cart-visible', 'mam-mobile-cart-enabled');
      document.body.style.removeProperty('--mam-mobile-cart-height');
    }
  });
}
